/*
  Storefront commerce events for PostHog.

  Event names match the old headless site (product_viewed, add_to_cart, cart_opened,
  checkout_initiated) so existing funnels keep working; theme events carry
  integration: 'shopify_theme' via posthog-analytics.js to tell the two apart.
  Checkout steps and purchase come from the Shopify custom pixel and order webhook,
  which read the _posthog_distinct_id / _posthog_session_id cart attributes set here.
*/
(() => {
  if (window.__moriAnalyticsInitialized) return;
  window.__moriAnalyticsInitialized = true;

  const contextElement = document.getElementById('MoriAnalyticsContext');
  let context = {};
  try {
    context = JSON.parse(contextElement?.textContent || '{}');
  } catch (error) {
    context = {};
  }

  const currency = context.currency || 'GBP';
  const productIdentity = context.product
    ? {
        product_id: context.product.product_id,
        product_handle: context.product.product_handle,
        product_title: context.product.product_title,
      }
    : {};
  const originalFetch = window.fetch.bind(window);
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const UTM_STORAGE_KEY = 'mori_utm';
  const UTM_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

  const toMoney = (cents) => Math.round(Number(cents || 0)) / 100;

  const compact = (object) =>
    Object.fromEntries(Object.entries(object).filter(([, value]) => value !== null && value !== undefined && value !== ''));

  const capture = (eventName, properties = {}, options) => {
    if (!window.posthog?.capture) return;
    window.posthog.capture(eventName, compact(properties), options);
  };

  const whenReady = (callback) => {
    if (window.__moriPostHogReady) {
      callback();
      return;
    }
    document.addEventListener('mori:posthog-ready', callback, { once: true });
  };

  /* ---------- Cart state ---------- */

  const frequencyWeeks = (planName) => {
    const match = String(planName || '').match(/(\d+)\s*week/i);
    return match ? Number(match[1]) : null;
  };

  const isGiftLine = (item) => Boolean(item?.properties?._mori_gift);

  const lineProperties = (item, quantity = item.quantity) => {
    const plan = item.selling_plan_allocation?.selling_plan;
    const unitPrice = toMoney(item.final_price ?? item.price);
    return compact({
      product_id: item.product_id,
      product_handle: item.handle,
      product_title: item.product_title,
      variant_id: item.variant_id,
      variant_title: item.variant_title,
      quantity,
      price: unitPrice,
      value: Math.round(unitPrice * quantity * 100) / 100,
      plan_type: plan ? 'subscribe' : 'one_time',
      selling_plan_id: plan?.id,
      selling_plan_name: plan?.name,
      subscription_frequency_weeks: frequencyWeeks(plan?.name),
    });
  };

  let cartSnapshot = context.cart || { items: [], attributes: {} };

  const cartProperties = (cart = cartSnapshot) => {
    const items = cart.items || [];
    const paidItems = items.filter((item) => !isGiftLine(item));
    return {
      // Same names the headless cart_opened / checkout_initiated events used.
      total_value: toMoney(cart.total_price),
      item_count: paidItems.reduce((count, item) => count + item.quantity, 0),
      line_count: paidItems.length,
      gift_line_count: items.length - paidItems.length,
      has_subscription: paidItems.some((item) => item.selling_plan_allocation),
      product_handles: [...new Set(paidItems.map((item) => item.handle))],
      currency: cart.currency || currency,
    };
  };

  const refreshCart = async () => {
    const response = await originalFetch(`${window.routes?.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (response.ok) cartSnapshot = await response.json();
    return cartSnapshot;
  };

  /* ---------- Attribution handed to checkout ---------- */

  /*
    One first-party cookie holds first-touch and last-touch for 30 days.
    - first: set on the first landing, never overwritten while the cookie lives.
    - last: replaced only by a landing with a campaign (utm_* or a Google click id)
      or an external referrer. A bare direct visit leaves it alone.
    posthog-analytics.js reads window.__moriAttribution for its super properties and the
    checkout pixel reads the cart attributes written below, so PostHog and orders agree.
  */
  const ATTRIBUTION_COOKIE = 'mori_attribution';

  /*
    Consent gate for attribution: the only consent check on the mori_attribution cookie and the
    attribution cart attributes. It always allows for now so every order keeps its source.
    When the cookie banner launches, this is where consent goes: return false until the visitor
    allows analytics (Shopify.customerPrivacy) and both writes stop.
  */
  const attributionConsentGate = () => true;
  const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid'];
  const CHECKOUT_HOSTS = /(^|\.)(shopify\.com|myshopify\.com|shop\.app|shopifypreview\.com)$/;

  // Broadest domain the browser accepts a cookie on, e.g. drinkmori.co.uk (co.uk is refused).
  const cookieDomain = (() => {
    const hostname = window.location.hostname;
    if (!hostname.includes('.') || /^[\d.]+$/.test(hostname)) return '';
    const parts = hostname.split('.');
    for (let index = parts.length - 2; index >= 0; index -= 1) {
      const domain = parts.slice(index).join('.');
      document.cookie = `mori_cookie_probe=1;domain=${domain};path=/;SameSite=Lax`;
      if (document.cookie.includes('mori_cookie_probe=1')) {
        document.cookie = `mori_cookie_probe=;domain=${domain};path=/;max-age=0`;
        return domain;
      }
    }
    return '';
  })();

  const readAttributionCookie = () => {
    try {
      const raw = document.cookie.split('; ').find((part) => part.startsWith(`${ATTRIBUTION_COOKIE}=`));
      return raw ? JSON.parse(decodeURIComponent(raw.slice(ATTRIBUTION_COOKIE.length + 1))) : null;
    } catch (error) {
      return null;
    }
  };

  const writeAttributionCookie = (value) => {
    document.cookie = [
      `${ATTRIBUTION_COOKIE}=${encodeURIComponent(JSON.stringify(value))}`,
      'path=/',
      `max-age=${UTM_MAX_AGE_MS / 1000}`,
      'SameSite=Lax',
      cookieDomain ? `domain=${cookieDomain}` : '',
      window.location.protocol === 'https:' ? 'Secure' : '',
    ]
      .filter(Boolean)
      .join(';');
  };

  const externalReferrer = () => {
    try {
      if (!document.referrer) return '';
      const host = new URL(document.referrer).hostname.replace(/^www\./, '');
      const ownHost = window.location.hostname.replace(/^www\./, '');
      if (host === ownHost || (cookieDomain && (host === cookieDomain || host.endsWith(`.${cookieDomain}`)))) return '';
      if (CHECKOUT_HOSTS.test(host)) return '';
      return host;
    } catch (error) {
      return '';
    }
  };

  // Google Shopping links repeat utm_* (product_sync first, the paid click last). Shopify keeps
  // the last value, so this does too. A Google click id means the visit was a paid click.
  const touchFromLanding = () => {
    const params = new URLSearchParams(window.location.search);
    const touch = {};
    UTM_KEYS.forEach((key) => {
      const value = params.getAll(key).filter(Boolean).pop();
      if (value) touch[key] = value.slice(0, 200);
    });
    const hasClickId = CLICK_ID_KEYS.some((key) => params.get(key));
    if (hasClickId) {
      if (touch.utm_medium !== 'cpc') touch.utm_medium = 'cpc';
      if (!touch.utm_source) touch.utm_source = 'google';
    }
    const isCampaign = hasClickId || UTM_KEYS.some((key) => touch[key]);
    const referrer = externalReferrer();
    if (referrer) touch.referrer_domain = referrer;
    touch.landing_page = window.location.pathname;
    return { touch, isNewTouch: isCampaign || Boolean(referrer) };
  };

  // Visitors from before the cookie existed have their UTMs in localStorage under mori_utm.
  const legacyTouch = () => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(UTM_STORAGE_KEY) || 'null');
      if (saved && Date.now() - saved.savedAt < UTM_MAX_AGE_MS) return saved.values;
    } catch (error) {
      /* storage can be blocked */
    }
    return null;
  };

  const resolveAttribution = () => {
    const { touch, isNewTouch } = touchFromLanding();
    const saved = readAttributionCookie();
    let first = saved?.first;
    let last = saved?.last;
    let changed = false;

    if (!first) {
      const legacy = legacyTouch();
      first = legacy || touch;
      last = isNewTouch || !legacy ? touch : legacy;
      changed = true;
    } else if (isNewTouch) {
      last = touch;
      changed = true;
    }

    try {
      if (changed && attributionConsentGate()) writeAttributionCookie({ first, last });
    } catch (error) {
      /* cookies can be blocked; this page still uses the values */
    }
    return { first: first || {}, last: last || {} };
  };

  const attributionSummary = (touch) => {
    if (touch.utm_source) {
      return [touch.utm_source, touch.utm_medium, touch.utm_campaign, touch.utm_content].filter(Boolean).join(' / ');
    }
    return touch.referrer_domain ? `${touch.referrer_domain} / referral` : 'direct';
  };

  const touches = resolveAttribution();

  const attribution = (() => {
    const { first, last } = touches;
    return compact({
      utm_source: last.utm_source,
      utm_medium: last.utm_medium,
      utm_campaign: last.utm_campaign,
      utm_content: last.utm_content,
      utm_term: last.utm_term,
      first_utm_source: first.utm_source,
      first_utm_medium: first.utm_medium,
      first_utm_campaign: first.utm_campaign,
      referrer_domain: last.referrer_domain,
      landing_page: last.landing_page || first.landing_page,
      attribution_summary: attributionSummary(last),
    });
  })();
  window.__moriAttribution = attribution;

  /*
    Plain channel names for email profiles (mori-popup-offer.js sends them to Klaviyo on sign-up).
    Google Shopping free listings arrive as google / product_sync and count as Google organic; a
    paid Google click is cpc (touchFromLanding sets that from the click id).
  */
  const PAID_MEDIUM = /^(cpc|ppc|paid.*|display|cpm)$/;
  const META_SOURCES = /^(meta|facebook|fb|instagram|ig|an|msg|threads)$/;
  const SOCIAL_HOSTS = /(^|\.)(facebook\.com|instagram\.com|threads\.net|tiktok\.com|pinterest\.[a-z.]+|reddit\.com|youtube\.com|x\.com|t\.co|linkedin\.com)$/;
  const AI_HOSTS = /(chatgpt\.com|openai\.com|perplexity\.ai|claude\.ai|gemini\.google\.com|copilot\.microsoft\.com)$/;
  const SEARCH_HOSTS = /(^|\.)(bing\.com|duckduckgo\.com|yahoo\.com|ecosia\.org|search\.brave\.com)$/;

  const channelFor = (touch) => {
    const source = (touch.utm_source || '').toLowerCase();
    const medium = (touch.utm_medium || '').toLowerCase();
    const referrer = (touch.referrer_domain || '').toLowerCase();
    const paid = PAID_MEDIUM.test(medium);
    if (source || medium) {
      if (medium === 'email' || source === 'klaviyo') return 'Email';
      if (META_SOURCES.test(source)) return paid ? 'Meta ads' : 'Social organic';
      if (source === 'google') return paid ? 'Google ads' : 'Google organic';
      if (source === 'tiktok') return paid ? 'TikTok ads' : 'Social organic';
      if (AI_HOSTS.test(source)) return 'AI assistant';
      if (/^(affiliate|referral|partner|popup)$/.test(medium)) return 'Partner sites';
      if (paid) return 'Other paid';
      if (medium === 'social') return 'Social organic';
      return 'Other campaign';
    }
    if (referrer) {
      if (/(^|\.)google\.[a-z.]+$/.test(referrer) && !AI_HOSTS.test(referrer)) return 'Google organic';
      if (AI_HOSTS.test(referrer)) return 'AI assistant';
      if (SEARCH_HOSTS.test(referrer)) return 'Other search';
      if (SOCIAL_HOSTS.test(referrer)) return 'Social organic';
      return 'Referral';
    }
    return 'Direct';
  };

  window.__moriSourceProfile = compact({
    first_source: attributionSummary(touches.first),
    first_channel: channelFor(touches.first),
    latest_source: attributionSummary(touches.last),
    latest_channel: channelFor(touches.last),
    first_landing_page: touches.first.landing_page,
  });

  /*
    Sends a sign-up's source to Klaviyo without anyone waiting on it. The popup redirects straight
    away, which can cut the request off, so the payload is kept in localStorage and sent once more
    from the next page, then dropped. klaviyo.identify's promise doesn't reliably settle, so it
    isn't used to decide; two identical updates are harmless. klaviyo.js loads late, so each send
    polls for it for a few seconds.
  */
  const KLAVIYO_PENDING_KEY = 'mori_klaviyo_source_pending';
  const KLAVIYO_SENDS = 2;

  const flushKlaviyoSource = (attempt = 0) => {
    let pending;
    try {
      pending = JSON.parse(window.localStorage.getItem(KLAVIYO_PENDING_KEY) || 'null');
    } catch (error) {
      return;
    }
    if (!pending?.profile) return;
    if (typeof window.klaviyo?.identify !== 'function') {
      if (attempt < 20) window.setTimeout(() => flushKlaviyoSource(attempt + 1), 500);
      return;
    }
    try {
      const sends = (pending.sends || 0) + 1;
      if (sends >= KLAVIYO_SENDS) window.localStorage.removeItem(KLAVIYO_PENDING_KEY);
      else window.localStorage.setItem(KLAVIYO_PENDING_KEY, JSON.stringify({ ...pending, sends }));
      window.klaviyo.identify(pending.profile);
    } catch (error) {
      /* storage or klaviyo.js failed; nothing else to try */
    }
  };

  window.__moriSendSourceToKlaviyo = (email) => {
    if (!email) return;
    const profile = { email, ...window.__moriSourceProfile };
    try {
      window.localStorage.setItem(KLAVIYO_PENDING_KEY, JSON.stringify({ profile, sends: 0 }));
    } catch (error) {
      try {
        window.klaviyo?.identify?.(profile);
      } catch (sendError) {
        /* storage blocked and klaviyo.js missing; nothing else to try */
      }
      return;
    }
    flushKlaviyoSource();
  };

  flushKlaviyoSource();

  const posthogActive = () =>
    Boolean(window.__moriPostHogReady && window.posthog?.get_distinct_id && !window.posthog.has_opted_out_capturing?.());

  /*
    The latest-visit fields are replaced as one set. Shopify keeps any cart attribute a write leaves
    out, so without this a visitor who came from matchaguide and later returned from ChatGPT ordered
    with utm_source chatgpt.com next to matchaguide's utm_medium and utm_campaign. Fields the latest
    visit doesn't have are sent blank, which Shopify treats as a delete. The first-visit fields are
    left alone: they are meant to keep the earlier source.
  */
  const LAST_TOUCH_KEYS = [...UTM_KEYS, 'referrer_domain'];
  const clearedLastTouch = Object.fromEntries(LAST_TOUCH_KEYS.filter((key) => !attribution[key]).map((key) => [key, '']));

  // Shopify drops blank attributes, so blank and missing are the same value.
  const sameAttribute = (current, desired) => (current ?? '') === (desired ?? '');

  // Attribution doesn't wait for PostHog, so an order keeps its source when PostHog is blocked.
  // The PostHog ids still need PostHog loaded and opted in, as before.
  const desiredCartAttributes = () => {
    const posthogIds = posthogActive()
      ? { _posthog_distinct_id: window.posthog.get_distinct_id(), _posthog_session_id: window.posthog.get_session_id?.() }
      : {};
    return {
      ...compact({ ...posthogIds, ...(attributionConsentGate() ? attribution : {}) }),
      ...(attributionConsentGate() ? clearedLastTouch : {}),
    };
  };

  /*
    Failed writes. The console warning always fires and is the last resort. PostHog gets an
    attribution_write_failed event when it is running; when it isn't (the failure we most want to
    see), the event waits in sessionStorage and is sent once PostHog loads on a later page.
  */
  const FAILURE_QUEUE_KEY = 'mori_attribution_failures';

  const reportAttributionFailure = (route, reason, detail = {}) => {
    const properties = compact({ route, reason, attribution_summary: attribution.attribution_summary, ...detail });
    console.warn('[mori] attribution write failed', properties);
    try {
      if (posthogActive()) {
        capture('attribution_write_failed', properties);
        return;
      }
      const queue = JSON.parse(window.sessionStorage.getItem(FAILURE_QUEUE_KEY) || '[]');
      queue.push({ ...properties, failed_at: new Date().toISOString() });
      window.sessionStorage.setItem(FAILURE_QUEUE_KEY, JSON.stringify(queue.slice(-10)));
    } catch (error) {
      /* storage blocked: the console warning above is all we can do */
    }
  };

  const flushAttributionFailures = () => {
    try {
      const queue = JSON.parse(window.sessionStorage.getItem(FAILURE_QUEUE_KEY) || '[]');
      window.sessionStorage.removeItem(FAILURE_QUEUE_KEY);
      queue.forEach((properties) => capture('attribution_write_failed', { ...properties, queued: true }));
    } catch (error) {
      /* nothing queued, or storage blocked */
    }
  };

  const postCartAttributes = async (attributes) => {
    const response = await originalFetch(`${window.routes?.cart_update_url || '/cart/update'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    });
    if (!response.ok) throw new Error(`cart/update.js returned ${response.status}`);
    cartSnapshot = await response.json();
  };

  // Only writes when the cart has items and something changed, so browsing never creates carts.
  // Writes run one at a time, retry once, and resolve false only when the write failed.
  let syncQueue = Promise.resolve(true);
  const syncCartAttributes = (route) => {
    syncQueue = syncQueue.then(async () => {
      if (!cartSnapshot.items?.length) return true;
      const desired = desiredCartAttributes();
      const current = cartSnapshot.attributes || {};
      if (Object.keys(desired).every((key) => sameAttribute(current[key], desired[key]))) return true;

      try {
        await postCartAttributes(desired);
        return true;
      } catch (error) {
        try {
          await postCartAttributes(desired);
          return true;
        } catch (retryError) {
          reportAttributionFailure(route, 'update_failed', { error: retryError.message });
          return false;
        }
      }
    });
    // Never leave the queue rejected, or every later write would be skipped.
    syncQueue = syncQueue.catch(() => false);
    return syncQueue;
  };

  /* ---------- Cart API observation ---------- */

  const readRequestBody = (body) => {
    if (!body) return {};
    if (body instanceof FormData || body instanceof URLSearchParams) {
      return {
        id: body.get('id'),
        line: body.get('line'),
        quantity: body.get('quantity'),
        items: body.get('id') ? [{ id: body.get('id'), quantity: body.get('quantity') || 1 }] : [],
      };
    }
    if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch (error) {
        return readRequestBody(new URLSearchParams(body));
      }
    }
    return {};
  };

  const handleAdd = (request, data) => {
    const requestedQuantity = {};
    (request.items || []).forEach((item) => {
      requestedQuantity[String(item.id)] = Number(item.quantity || 1);
    });

    const addedItems = (data.items || (data.variant_id ? [data] : [])).filter((item) => !isGiftLine(item));
    if (!addedItems.length) return;

    const lines = addedItems.map((item) => lineProperties(item, requestedQuantity[String(item.variant_id)] || 1));
    capture('add_to_cart', {
      ...lines[0],
      quantity: lines.reduce((sum, line) => sum + line.quantity, 0),
      value: Math.round(lines.reduce((sum, line) => sum + line.value, 0) * 100) / 100,
      currency,
      line_count: lines.length,
      products: lines,
    });
  };

  const handleChange = (request, data) => {
    const previousItems = cartSnapshot.items || [];
    const quantity = Number(request.quantity);
    let previous;
    if (request.line) previous = previousItems[Number(request.line) - 1];
    else if (request.id) {
      previous = previousItems.find((item) => item.key === request.id || String(item.variant_id) === String(request.id));
    }

    if (previous && !isGiftLine(previous) && !Number.isNaN(quantity) && quantity !== previous.quantity) {
      if (quantity === 0) {
        capture('cart_item_removed', { ...lineProperties(previous), currency });
      } else {
        capture('cart_item_quantity_changed', {
          ...lineProperties(previous, quantity),
          previous_quantity: previous.quantity,
          currency,
        });
      }
    }
  };

  const CART_ENDPOINT = /\/cart\/(add|change|update)(\.js)?$/;

  window.fetch = function moriObservedFetch(input, init) {
    const result = originalFetch(input, init);
    try {
      const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, window.location.origin);
      const method = (init?.method || input?.method || 'GET').toUpperCase();
      const match = method === 'POST' && url.origin === window.location.origin && url.pathname.match(CART_ENDPOINT);

      if (match) {
        const request = readRequestBody(init?.body);
        result
          .then((response) => (response.ok ? response.clone().json() : null))
          .then(async (data) => {
            if (!data || data.status) return;
            if (match[1] === 'add') {
              handleAdd(request, data);
              await refreshCart();
            } else {
              if (match[1] === 'change') handleChange(request, data);
              if (data.items) cartSnapshot = data;
            }
            await syncCartAttributes(`cart_${match[1]}`);
          })
          .catch(() => {});
      }
    } catch (error) {
      /* never let tracking break the cart */
    }
    return result;
  };

  /* ---------- Cart drawer, cart page and checkout ---------- */

  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    let wasOpen = cartDrawer.classList.contains('active');
    new MutationObserver(() => {
      const isOpen = cartDrawer.classList.contains('active');
      if (isOpen && !wasOpen) capture('cart_opened', { surface: 'drawer', ...cartProperties() });
      wasOpen = isOpen;
    }).observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
  }

  document.addEventListener('submit', async (event) => {
    const submitter = event.submitter;
    const form = event.target;
    if (submitter?.name !== 'checkout' || form.dataset.moriCheckoutTracked === 'true') return;

    capture(
      'checkout_initiated',
      { surface: form.id === 'CartDrawer-Form' ? 'drawer' : 'page', ...cartProperties() },
      { transport: 'sendBeacon' }
    );

    const route = { 'CartDrawer-Form': 'checkout_drawer', 'cart-notification-form': 'checkout_notification' }[form.id] || 'checkout_page';
    const desired = desiredCartAttributes();
    const current = cartSnapshot.attributes || {};
    if (Object.keys(desired).every((key) => sameAttribute(current[key], desired[key]))) return;

    event.preventDefault();
    form.dataset.moriCheckoutTracked = 'true';
    await holdForAttribution(route);
    form.requestSubmit(submitter);
  });

  // Apple Pay / Google Pay in the drawer open a wallet sheet rather than submitting the form, so
  // the submit hook never sees them. Count the tap as a checkout start with its own surface. The
  // cart attributes are already synced on every cart change, and the sheet must open inside the
  // tap, so there's nothing to hold for.
  const EXPRESS_WALLETS = {
    'shopify-apple-pay-button': 'apple_pay',
    'shopify-google-pay-button': 'google_pay',
    'shop-pay-wallet-button': 'shop_pay',
    'shopify-paypal-button': 'paypal',
  };
  document.addEventListener(
    'click',
    (event) => {
      const wallet = event.target.closest?.('.mori-cart__express shopify-accelerated-checkout-cart > *');
      if (!wallet) return;
      capture(
        'checkout_initiated',
        { surface: 'drawer_express', express_wallet: EXPRESS_WALLETS[wallet.tagName.toLowerCase()] || wallet.tagName.toLowerCase(), ...cartProperties() },
        { transport: 'sendBeacon' }
      );
    },
    true
  );

  // Hold the redirect briefly so checkout (and the pixel/webhook) sees this visitor's id and source.
  async function holdForAttribution(route) {
    const result = await Promise.race([
      syncCartAttributes(route).catch(() => false),
      new Promise((resolve) => setTimeout(() => resolve('timeout'), 1500)),
    ]);
    if (result === 'timeout') reportAttributionFailure(route, 'timeout_at_checkout');
    else if (result && attributionConsentGate() && !cartSnapshot.attributes?.attribution_summary) {
      reportAttributionFailure(route, 'missing_at_checkout');
    }
  }

  // The direct-to-checkout test arm skips the drawer's checkout button, so product-form.js calls
  // this instead before it redirects. It fires the same checkout_initiated event with surface
  // 'direct', so existing funnels count both arms.
  window.__moriPrepareDirectCheckout = async () => {
    await refreshCart().catch(() => cartSnapshot);
    capture('checkout_initiated', { surface: 'direct', ...cartProperties() }, { transport: 'sendBeacon' });
    await holdForAttribution('checkout_direct');
  };

  /* ---------- Product page purchase options ---------- */

  let optionTimer;
  document.addEventListener('change', (event) => {
    const target = event.target;
    const purchaseBuilder = target.closest?.('mori-purchase-builder');
    const giftSetBuilder = target.closest?.('mori-gift-set-builder');
    if (!purchaseBuilder && !giftSetBuilder) return;

    window.clearTimeout(optionTimer);
    optionTimer = window.setTimeout(() => {
      if (purchaseBuilder) {
        const amount = purchaseBuilder.querySelector('[name^="mori-amount"]:checked');
        const subscribing = purchaseBuilder.querySelector('[value="subscription"]')?.checked === true;
        const frequency = purchaseBuilder.querySelector('[name^="mori-frequency"]:checked');
        let option = 'amount';
        if (target.name?.startsWith('mori-plan')) option = 'plan';
        if (target.name?.startsWith('mori-frequency')) option = 'frequency';

        capture('purchase_option_selected', {
          builder: 'matcha',
          option_changed: option,
          ...productIdentity,
          plan_type: subscribing ? 'subscribe' : 'one_time',
          tins: Number(amount?.dataset.tins || 1),
          variant_id: amount?.value,
          subscription_frequency_weeks: subscribing ? frequencyWeeks(frequency?.value) : null,
          price: toMoney(amount?.dataset[subscribing ? 'subscriptionCents' : 'onceCents']),
          currency,
        });
      } else {
        const subscribing = giftSetBuilder.querySelector('[value="subscribe"]')?.checked === true;
        const frequency = giftSetBuilder.querySelector('[name^="mori-gift-frequency"]:checked');
        // The gift set's frequency input carries the selling plan id, not its name, so the
        // week count comes from the label text. Keeps this comparable with the matcha builder.
        const frequencyLabel = frequency
          ? giftSetBuilder.querySelector(`label[for="${frequency.id}"]`)?.textContent.trim()
          : null;
        capture('purchase_option_selected', {
          builder: 'gift_set',
          option_changed: target.name?.startsWith('mori-gift-frequency') ? 'frequency' : 'plan',
          ...productIdentity,
          plan_type: subscribing ? 'subscribe' : 'one_time',
          selling_plan_id: subscribing ? frequency?.value : null,
          subscription_frequency_weeks: subscribing ? frequencyWeeks(frequencyLabel) : null,
          currency,
        });
      }
    }, 50);
  });

  /* ---------- Page-level events ---------- */

  whenReady(() => {
    if (context.product) {
      capture('product_viewed', {
        ...context.product,
        price: toMoney(context.product.price),
        compare_at_price: context.product.compare_at_price ? toMoney(context.product.compare_at_price) : null,
        currency,
      });
    }

    if (context.collection) capture('collection_viewed', context.collection);

    if (context.search) capture('products_searched', context.search);

    if (context.template === 'cart') capture('cart_opened', { surface: 'page', ...cartProperties() });

    flushAttributionFailures();
    syncCartAttributes('posthog_ready').catch(() => {});
  });

  // A cart that already has items gets its attribution now, without waiting for PostHog. This also
  // covers the Shop Pay / Apple Pay / Google Pay buttons on the cart page, which skip the submit hook.
  syncCartAttributes('page_load').catch(() => {});
})();
